import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { HardDriveIcon, AlertTriangleIcon, RefreshCwIcon, SearchIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBytes = (bytes) => {
  if (!bytes || bytes <= 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 2 : v < 100 ? 1 : 0)} ${u[i]}`;
};

const UltraStorageUsage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | warning | critical | unlimited

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const r = await axios.get(`${BACKEND_URL}/api/ultra-admin/storage-usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(r.data.companies || []);
    } catch (e) {
      console.error('storage usage error', e);
      toast.error('Depolama verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const summary = {
    total: companies.length,
    warning: companies.filter(c => c.is_warning).length,
    critical: companies.filter(c => c.is_critical).length,
    unlimited: companies.filter(c => c.is_unlimited).length,
    totalUsedBytes: companies.reduce((s, c) => s + (c.storage_used_bytes || 0), 0),
  };

  const filtered = companies.filter(c => {
    if (filter === 'warning' && !c.is_warning) return false;
    if (filter === 'critical' && !c.is_critical) return false;
    if (filter === 'unlimited' && !c.is_unlimited) return false;
    if (!query) return true;
    return (c.company_name || '').toLowerCase().includes(query.toLowerCase()) ||
           (c.plan_slug || '').toLowerCase().includes(query.toLowerCase());
  });

  return (
    <div className="p-6 space-y-6" data-testid="ultra-storage-usage">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <HardDriveIcon className="w-8 h-8 text-purple-600" />
            Depolama Kullanımı
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Tüm şirketlerin plan bazlı depolama tüketimi · Uyarı eşikleri: %80 sarı, %95 kırmızı
          </p>
        </div>
        <Button
          onClick={fetchData}
          disabled={loading}
          data-testid="storage-refresh-btn"
          variant="outline"
        >
          <RefreshCwIcon className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="text-xs text-gray-500">Toplam Şirket</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{summary.total}</div>
        </Card>
        <Card
          className={`p-4 cursor-pointer transition ${filter === 'warning' ? 'ring-2 ring-amber-400' : ''}`}
          onClick={() => setFilter(filter === 'warning' ? 'all' : 'warning')}
          data-testid="filter-warning"
        >
          <div className="text-xs text-amber-700">⚠️ Uyarı (≥%80)</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{summary.warning}</div>
        </Card>
        <Card
          className={`p-4 cursor-pointer transition ${filter === 'critical' ? 'ring-2 ring-red-400' : ''}`}
          onClick={() => setFilter(filter === 'critical' ? 'all' : 'critical')}
          data-testid="filter-critical"
        >
          <div className="text-xs text-red-700">🚨 Kritik (≥%95)</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{summary.critical}</div>
        </Card>
        <Card
          className={`p-4 cursor-pointer transition ${filter === 'unlimited' ? 'ring-2 ring-blue-400' : ''}`}
          onClick={() => setFilter(filter === 'unlimited' ? 'all' : 'unlimited')}
          data-testid="filter-unlimited"
        >
          <div className="text-xs text-blue-700">∞ Limitsiz</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">{summary.unlimited}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-gray-500">Toplam Kullanım</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{fmtBytes(summary.totalUsedBytes)}</div>
        </Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Şirket adı veya plan ara..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="storage-search"
          />
        </div>
        {filter !== 'all' && (
          <Button variant="ghost" onClick={() => setFilter('all')} data-testid="clear-filter">
            Filtreyi temizle ({filtered.length})
          </Button>
        )}
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Şirket</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Plan</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Kullanılan</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Limit</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 w-1/3">Kullanım %</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-700">Durum</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-500">Yükleniyor...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400">Sonuç bulunamadı</td></tr>
              ) : (
                filtered.map((c) => {
                  const pct = c.usage_percentage || 0;
                  const barColor = c.is_critical
                    ? 'bg-red-500'
                    : c.is_warning
                    ? 'bg-amber-400'
                    : 'bg-emerald-500';
                  return (
                    <tr key={c.company_id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{c.company_name}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="capitalize">{c.plan_slug}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        {fmtBytes(c.storage_used_bytes)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        {c.is_unlimited ? '∞' : `${c.storage_limit_gb} GB`}
                      </td>
                      <td className="px-4 py-3">
                        {c.is_unlimited ? (
                          <span className="text-xs text-blue-600">Limitsiz</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                              <div
                                className={`h-full ${barColor} transition-all`}
                                style={{ width: `${Math.min(100, pct)}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium text-gray-700 w-12 text-right">
                              %{pct.toFixed(1)}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {c.is_critical ? (
                          <Badge className="bg-red-100 text-red-700 border-red-200">
                            <AlertTriangleIcon className="w-3 h-3 mr-1" />
                            Kritik
                          </Badge>
                        ) : c.is_warning ? (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                            Uyarı
                          </Badge>
                        ) : c.is_unlimited ? (
                          <Badge variant="outline" className="text-blue-600">Limitsiz</Badge>
                        ) : (
                          <Badge variant="outline" className="text-emerald-600">OK</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default UltraStorageUsage;
